import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "@/utils/cx";
import { TABLE_ICON_GUTTER_WIDTH_PX } from "./table-column-layout";
import { TableHeaderCell, TableHeaderCellSortable } from "./table-header-cell";
import { TableSelectionCellHeader } from "./table-selection-cell-header";

export const TableHeaderRowType = {
	Default: "default",
	Job: "job",
	Ticket: "ticket",
	Record: "record",
	Asset: "asset",
	Assignees: "assignees",
} as const;

export type TableHeaderRowType =
	(typeof TableHeaderRowType)[keyof typeof TableHeaderRowType];

export interface TableHeaderRowColumn {
	label: string;
	overline?: string;
	hasOverline?: boolean;
	hasTooltip?: boolean;
	tooltip?: string;
	sortable?: (typeof TableHeaderCellSortable)[keyof typeof TableHeaderCellSortable];
	/** Narrow leading icon gutter (Figma entity tables). */
	isIconGutter?: boolean;
}

export interface TableHeaderRowProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
	/**
	 * Show the leading select-all checkbox column (Figma `Selectable`).
	 * On by default.
	 */
	selectable?: boolean;
	/**
	 * Entity shortcut for design-system demos (Figma `Type`).
	 * Only `default` is required in product — live tables pass `children` / column config.
	 */
	type?: TableHeaderRowType;
	/** Show the bottom border under the row (Figma bottom border). */
	bordered?: boolean;
	/**
	 * Header cell content. When omitted, renders preset columns for `type`
	 * (Figma entity shortcuts for layout work).
	 */
	children?: ReactNode;
	/** Optional column presets override when `children` is omitted. */
	columns?: TableHeaderRowColumn[];
}

const defaultColumns: TableHeaderRowColumn[] = [
	{
		label: "Label",
		overline: "Overline",
		hasOverline: true,
		hasTooltip: true,
		tooltip: "More info",
		sortable: TableHeaderCellSortable.None,
	},
	{
		label: "Label",
		overline: "Overline",
		hasOverline: true,
		hasTooltip: true,
		tooltip: "More info",
		sortable: TableHeaderCellSortable.None,
	},
	{
		label: "Label",
		overline: "Overline",
		hasOverline: true,
		hasTooltip: true,
		tooltip: "More info",
		sortable: TableHeaderCellSortable.None,
	},
];

function entityColumns(labels: string[]): TableHeaderRowColumn[] {
	return labels.map((label) => ({
		label,
		sortable: TableHeaderCellSortable.Unsorted,
	}));
}

/** Leading unlabeled column that sits above the featured-icon cell. */
const iconGutterColumn: TableHeaderRowColumn = {
	label: "",
	sortable: TableHeaderCellSortable.None,
	isIconGutter: true,
};

function entityColumnsWithIcon(labels: string[]): TableHeaderRowColumn[] {
	return [iconGutterColumn, ...entityColumns(labels)];
}

/**
 * Inner HeaderRow strip — h-40, bg-tertiary (Figma HeaderRow).
 * Shared by presentational {@link TableHeaderRow} and live `CustomTable.Header`.
 */
export const tableHeaderRowInnerClassName =
	"relative flex h-10 items-stretch overflow-x-clip overflow-y-visible bg-tertiary";

/**
 * Aria `<thead>` HeaderRow chrome (Figma 3048:365).
 * Bottom border is painted on `th` so it spans sticky columns.
 */
export function tableHeaderRowAriaClassName(options: {
	bordered?: boolean;
	/** Kept for call-site parity with presentational HeaderRow `selectable`. */
	selectable?: boolean;
}): string {
	const { bordered = true } = options;
	void options.selectable;
	return cx(
		"relative h-10 bg-tertiary",
		bordered &&
			"[&>tr>th]:after:pointer-events-none [&>tr>th]:after:absolute [&>tr>th]:after:inset-x-0 [&>tr>th]:after:bottom-0 [&>tr>th]:after:h-px [&>tr>th]:after:bg-border-secondary [&>tr>th]:focus-visible:after:bg-transparent",
		// Edge inset is HeaderCell padding (px-6) / BodyCell px-5 — do not add
		// last-th pr-8; it created empty trailing width past column content.
	);
}

/** Bottom border under the presentational (div) HeaderRow. */
export function TableHeaderRowBottomBorder() {
	return (
		<div
			className="h-px w-full shrink-0 bg-border-secondary"
			aria-hidden
			data-name="Bottom border"
		/>
	);
}

/** Figma Type=Job / Ticket / Record / Asset / Assignees column shortcuts. */
const typeColumns: Record<TableHeaderRowType, TableHeaderRowColumn[]> = {
	default: defaultColumns,
	job: entityColumnsWithIcon([
		"Number",
		"Name",
		"Description",
		"Status",
		"Type",
		"Sub-Organization",
		"Users",
		"Subcontractors",
		"Created",
		"Updated",
	]),
	ticket: entityColumnsWithIcon([
		"Number",
		"Name",
		"Description",
		"Status",
		"Type",
		"Priority",
		"Sub-Organization",
		"Assignee",
		"Due Date",
		"Created",
		"Updated",
	]),
	record: entityColumnsWithIcon([
		"Number",
		"Name",
		"Description",
		"Type",
		"Sub-Organization",
		"Created",
		"Updated",
	]),
	asset: entityColumnsWithIcon([
		"Number",
		"Name",
		"Description",
		"Status",
		"Type",
		"Sub-Organization",
		"Created",
		"Updated",
	]),
	assignees: entityColumns(["Name", "Role", "Actions"]),
};

/**
 * Presentational table header row matching Figma Table/HeaderRow (3048:365).
 * Live React Aria tables use `CustomTable.Header` for the same chrome; use this
 * shell outside Aria / in demos. Entity `type` presets are design shortcuts —
 * product tables should pass `children` from column configuration.
 */
export function TableHeaderRow({
	selectable = true,
	type = TableHeaderRowType.Default,
	bordered = true,
	children,
	columns,
	className,
	...props
}: TableHeaderRowProps) {
	const presetColumns = columns ?? typeColumns[type];

	return (
		<div
			{...props}
			data-selectable={selectable}
			data-type={type}
			data-slot="table-header-row"
			className={cx("relative flex w-full flex-col items-stretch", className)}
		>
			<div
				className={cx(tableHeaderRowInnerClassName, !selectable && "pl-8 pr-8")}
			>
				{selectable ? <TableSelectionCellHeader /> : null}
				{children ??
					presetColumns.map((column, index) => (
						<TableHeaderCell
							key={`${column.label || "icon"}-${index}`}
							label={column.label}
							overline={column.overline}
							hasOverline={column.hasOverline}
							hasTooltip={column.hasTooltip}
							tooltip={column.tooltip}
							sortable={column.sortable}
							className={
								column.isIconGutter
									? "shrink-0 max-w-none px-5"
									: "w-[200px] shrink-0"
							}
							style={
								column.isIconGutter
									? { width: TABLE_ICON_GUTTER_WIDTH_PX }
									: undefined
							}
						/>
					))}
			</div>
			{bordered ? <TableHeaderRowBottomBorder /> : null}
		</div>
	);
}
